import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import cvService from '../services/cvService';
import { CVData, SectionKey } from '../types/cv';
import {
    ensureCVIdsAndLayout,
    moveItemToPage,
    moveSectionToPage,
    setPageCount,
    resetAllItemsToFirstPage,
} from '../utils/cvPagination';
import { clearDraft, loadDraft, moveDraftToId, saveDraft } from '../utils/cvDraft';
import { useAuth } from '../context/AuthContext';
import { compressImageDataUrl, estimateDataUrlBytes } from '../utils/compressImage';

/** Prépare le CV pour l’API : compresse une photo trop lourde si besoin. */
async function prepareCVPayload(data: CVData): Promise<{ payload: CVData; didCompress: boolean }> {
    const img = data.personalInfo?.profileImage;
    if (!img || !img.startsWith('data:image/') || estimateDataUrlBytes(img) < 200_000) {
        return { payload: ensureCVIdsAndLayout(data), didCompress: false };
    }
    const compressed = await compressImageDataUrl(img);
    if (compressed === img) {
        return { payload: ensureCVIdsAndLayout(data), didCompress: false };
    }
    return {
        payload: ensureCVIdsAndLayout({
            ...data,
            personalInfo: { ...data.personalInfo, profileImage: compressed },
        }),
        didCompress: true,
    };
}

export const initialData: CVData = ensureCVIdsAndLayout({
    title: 'Mon CV Professionnel',
    personalInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: "Côte d'Ivoire",
        profession: '',
        summary: '',
    },
    education: [],
    experience: [],
    skills: [],
    languages: [],
    interests: [],
    references: [],
    layout: { pageCount: 1, itemPages: {}, sectionPages: {} },
});

export const useCVBuilder = (
    cvIdFromUrl: string | null,
    token: string | null,
    templateFromUrl: string | null = null
) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const readUserId = (): string | null => {
        if (user?._id) return user._id;
        try {
            const raw = localStorage.getItem('fcv_user') || localStorage.getItem('user');
            if (!raw) return null;
            return (JSON.parse(raw) as { _id?: string })._id || null;
        } catch {
            return null;
        }
    };

    const userId = readUserId();

    const bootDraft = cvIdFromUrl
        ? loadDraft(userId, cvIdFromUrl)
        : templateFromUrl
          ? null
          : loadDraft(userId, null);

    const [data, setData] = useState<CVData>(
        () => bootDraft?.data || initialData
    );
    const [template, setTemplate] = useState(
        () => bootDraft?.template || templateFromUrl || 'ocean-wave'
    );
    const [savedCVId, setSavedCVId] = useState<string | null>(
        () => cvIdFromUrl || bootDraft?.cvId || null
    );
    const [isSaving, setIsSaving] = useState(false);
    const [hasPaid, setHasPaid] = useState(false);
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [isHydrated, setIsHydrated] = useState(false);

    const dataRef = useRef(data);
    const templateRef = useRef(template);
    const savedIdRef = useRef(savedCVId);
    dataRef.current = data;
    templateRef.current = template;
    savedIdRef.current = savedCVId;

    // Charger depuis l'API si id présent ; sinon template URL ou brouillon ; sinon galerie
    useEffect(() => {
        let cancelled = false;

        const hydrate = async () => {
            if (cvIdFromUrl && token) {
                try {
                    const local = loadDraft(userId, cvIdFromUrl);
                    const cv = await cvService.getCV(cvIdFromUrl);
                    if (cancelled) return;
                    const { template: tpl, _id, updatedAt, ...rest } = cv as CVData & {
                        template: string;
                        _id: string;
                        updatedAt?: string;
                    };
                    const removedTemplates = ['bold-curves', 'minimal-dark', 'modern'];
                    const nextTpl = removedTemplates.includes(tpl) ? 'ocean-wave' : tpl;
                    const serverData = ensureCVIdsAndLayout(rest as CVData);

                    const serverTime = updatedAt ? new Date(updatedAt).getTime() : 0;
                    const localNewer =
                        local &&
                        local.cvId === _id &&
                        local.updatedAt > serverTime &&
                        ((local.data.layout?.pageCount || 1) > 1 ||
                            Object.values(local.data.layout?.itemPages || {}).some((p) => p > 0));

                    if (localNewer) {
                        // Garder le brouillon multi-pages plus récent que le serveur
                        setData(local.data);
                        setTemplate(local.template || nextTpl);
                        setSavedCVId(_id);
                    } else {
                        setData(serverData);
                        setTemplate(nextTpl);
                        setSavedCVId(_id);
                        saveDraft(userId, _id, serverData, nextTpl);
                    }
                } catch (error) {
                    console.error('Error fetching CV:', error);
                }
            } else if (!cvIdFromUrl && templateFromUrl) {
                const draft = loadDraft(userId, null);
                if (!cancelled) {
                    if (draft && !draft.cvId) {
                        setData(draft.data);
                        setTemplate(templateFromUrl);
                        setSavedCVId(null);
                    } else {
                        setData(initialData);
                        setTemplate(templateFromUrl);
                        setSavedCVId(null);
                        setHasPaid(false);
                    }
                }
            } else if (!cvIdFromUrl && !templateFromUrl) {
                const draft = loadDraft(userId, null);
                if (draft?.cvId && !cancelled) {
                    setData(draft.data);
                    setTemplate(draft.template);
                    setSavedCVId(draft.cvId);
                    navigate(`/builder?id=${draft.cvId}`, { replace: true });
                } else if (!cancelled) {
                    navigate('/choix-modele', { replace: true });
                    return;
                }
            }
            if (!cancelled) setIsHydrated(true);
        };

        hydrate();
        return () => {
            cancelled = true;
        };
    }, [cvIdFromUrl, templateFromUrl, token, userId, navigate]);

    // Autosave local à chaque modification
    useEffect(() => {
        if (!isHydrated) return;
        const t = window.setTimeout(() => {
            saveDraft(userId, savedCVId, data, template);
        }, 400);
        return () => clearTimeout(t);
    }, [data, template, savedCVId, userId, isHydrated]);

    // Autosave serveur (silencieux) si le CV existe déjà
    useEffect(() => {
        if (!isHydrated || !token || !savedCVId) return;
        const t = window.setTimeout(async () => {
            try {
                const { payload, didCompress } = await prepareCVPayload(dataRef.current);
                if (didCompress) {
                    setData(payload);
                    saveDraft(userId, savedCVId, payload, templateRef.current);
                }
                await cvService.updateCV(savedCVId, payload, templateRef.current);
            } catch (e) {
                console.warn('Autosave serveur échoué (brouillon local conservé)', e);
            }
        }, 2500);
        return () => clearTimeout(t);
    }, [data, template, savedCVId, token, isHydrated, userId]);

    // Sauvegarder aussi juste avant de quitter / refresh
    useEffect(() => {
        const persist = () => {
            saveDraft(userId, savedIdRef.current, dataRef.current, templateRef.current);
        };
        window.addEventListener('beforeunload', persist);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') persist();
        });
        return () => {
            window.removeEventListener('beforeunload', persist);
            persist();
        };
    }, [userId]);

    const handleChange = (section: keyof CVData, value: any) => {
        setData((prev) => ensureCVIdsAndLayout({ ...prev, [section]: value }));
    };

    const handleLayoutChange = useCallback((next: CVData) => {
        setData(ensureCVIdsAndLayout(next));
    }, []);

    const handleMoveItem = useCallback((itemId: string, pageIndex: number) => {
        setData((prev) => {
            let next = moveItemToPage(prev, itemId, pageIndex);
            if ((next.layout?.pageCount || 1) < pageIndex + 1) {
                next = {
                    ...next,
                    layout: {
                        ...next.layout!,
                        pageCount: pageIndex + 1,
                    },
                };
            }
            return ensureCVIdsAndLayout(next);
        });
    }, []);

    const handleMoveSection = useCallback((section: SectionKey, pageIndex: number) => {
        setData((prev) => {
            let next = moveSectionToPage(prev, section, pageIndex);
            if ((next.layout?.pageCount || 1) < pageIndex + 1) {
                next = {
                    ...next,
                    layout: {
                        ...next.layout!,
                        pageCount: pageIndex + 1,
                    },
                };
            }
            return ensureCVIdsAndLayout(next);
        });
    }, []);

    const handleSetPageCount = useCallback((pageCount: number) => {
        setData((prev) => setPageCount(prev, pageCount));
    }, []);

    const handleResetLayout = useCallback(() => {
        setData((prev) => resetAllItemsToFirstPage(prev));
    }, []);

    const handleSaveCV = async (showNotification = true) => {
        setIsSaving(true);
        try {
            const { payload } = await prepareCVPayload(data);
            let result;
            const previousId = savedCVId;
            if (savedCVId) {
                result = await cvService.updateCV(savedCVId, payload, template);
            } else {
                result = await cvService.createCV(payload, template);
            }

            setSavedCVId(result._id);
            setData(ensureCVIdsAndLayout(payload));
            moveDraftToId(userId, previousId, result._id, payload, template);

            if (showNotification) {
                setShowSaveSuccess(true);
                setTimeout(() => setShowSaveSuccess(false), 3000);
            }

            if (!previousId || previousId !== result._id) {
                navigate(`/builder?id=${result._id}`, { replace: true });
            }
            return result._id;
        } catch (error: any) {
            console.error('Save error:', error);
            // Même en cas d'erreur réseau, le brouillon local reste
            saveDraft(userId, savedCVId, data, template);
            return null;
        } finally {
            setIsSaving(false);
        }
    };

    const handleSelectCV = (id: string) => {
        navigate(`/builder?id=${id}`, { replace: true });
    };

    const handleNewCV = () => {
        clearDraft(userId, savedCVId);
        clearDraft(userId, null);
        setData(initialData);
        setTemplate('ocean-wave');
        setSavedCVId(null);
        setHasPaid(false);
        navigate('/choix-modele', { replace: true });
    };

    return {
        data,
        setData,
        template,
        setTemplate,
        savedCVId,
        isSaving,
        hasPaid,
        setHasPaid,
        showSaveSuccess,
        handleChange,
        handleLayoutChange,
        handleMoveItem,
        handleMoveSection,
        handleSetPageCount,
        handleResetLayout,
        handleSaveCV,
        handleSelectCV,
        handleNewCV,
    };
};
